import * as React from 'react';
import * as styles from './styles.scss';
import { AccountType } from 'constants/enum';
import { AccountDropdown, Fallback } from 'components/common';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { IState } from 'redux/global-reducers';
import { RouteComponentProps, withRouter } from 'react-router';
import { WithNamespaces, withNamespaces } from 'react-i18next';
import { connect } from 'react-redux';
import { formatNumber, formatStringToNumber, handleError } from 'utils/common';
import { getAssetInformation } from './actions';
import { withErrorBoundary } from 'react-error-boundary';
import config from 'config';
import Portfolio from '../../Portfolio';
import HighchartsReact from 'highcharts-react-official';
import Highcharts, { Point, SeriesOptionsType } from 'highcharts/highstock';
import { Refresh } from 'assets/svg';
import { enquiryPortfolio } from 'components/common/OrderForm/actions';
import { InfoBuyIn } from 'assets/svg';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

interface IAssetInforEquityProps
  extends React.ClassAttributes<AssetInforEquityComponent>,
    WithNamespaces,
    RouteComponentProps {
  readonly selectedAccount: IState['selectedAccount'];
  readonly assetInformation: IState['assetInformation'];
  readonly equityPortfolio: IState['equityPortfolio'];
  readonly lang: IState['lang'];
  readonly isSmallComponent?: boolean;

  readonly getAssetInformation: typeof getAssetInformation;
  readonly enquiryPortfolio: typeof enquiryPortfolio;
}
interface IAssetInforEquityState {
  readonly chartOption: Highcharts.Options;
  readonly isCollapsed: {
    cash: boolean;
    pendCashSold: boolean;
    marginInfo: boolean;
  };
}
class AssetInforEquityComponent extends React.Component<
  IAssetInforEquityProps,
  IAssetInforEquityState
> {
  private chartRef: React.RefObject<HighchartsReact>;
  constructor(props: IAssetInforEquityProps) {
    super(props);
    this.chartRef = React.createRef();
    this.state = {
      isCollapsed: { cash: true, pendCashSold: true, marginInfo: true },
      chartOption: {
        chart: {
          plotBorderWidth: 0,
          plotShadow: false,
          type: 'pie',
          spacing: [0, 0, 0, 0],
          backgroundColor: undefined,
        },
        tooltip: {
          pointFormat: '{series.name}: <b>{point.percentage:.2f}%</b>',
        },
        plotOptions: {
          series: {
            borderWidth: 0,
          },
          pie: {
            allowPointSelect: true,
            cursor: 'pointer',
            size: 220,
            dataLabels: {
              enabled: true,
              useHTML: true,
              format:
                '<div><span style="color: white;">{point.name}</span><br><span style="color: white;">{point.percentage:.2f}%</span></div>',
            },
            showInLegend: true,
            borderWidth: 0,
          },
        },
        subtitle: {
          useHTML: true,
          verticalAlign: 'middle',
          y: -15,
          x: -100,
        },
        legend: {
          layout: 'vertical',
          verticalAlign: 'middle',
          align: 'right',
          useHTML: true,
          enabled: true,

          labelFormatter: function () {
            return `<span  style="color: white;width: 150px; display:inline-block; border-bottom: 1px solid #494a4b;"><span style="float:left; font-size:14px; font-weight:normal" >${
              (this as Point).name
            }</span><span style="float:right">${(this as Point).percentage.toFixed(
              2
            )}%</span></span>`;
          },
        },
        series: [
          {
            name: 'Share',
            type: 'pie',
            innerSize: '60%',
            borderWidth: 0,
          },
        ] as SeriesOptionsType[],
      },
    };
  }

  componentDidMount() {
    if (this.props.selectedAccount != null) {
      if (this.props.selectedAccount.isBrokerCall) {
        this.props.getAssetInformation({
          accountNumber: this.props.selectedAccount.accountNumber,
          clientID: this.props.selectedAccount.clientID,
        });
      } else {
        this.props.getAssetInformation({
          accountNumber: this.props.selectedAccount.accountNumber,
        });
      }
    }
  }

  shouldComponentUpdate(nextProps: IAssetInforEquityProps) {
    if (
      this.props.selectedAccount !== nextProps.selectedAccount &&
      nextProps.selectedAccount !== null
    ) {
      if (this.props.selectedAccount?.isBrokerCall) {
        this.props.getAssetInformation({
          accountNumber: nextProps.selectedAccount.accountNumber,
          clientID: nextProps.selectedAccount.clientID,
        });
      } else {
        this.props.getAssetInformation({
          accountNumber: nextProps.selectedAccount.accountNumber,
        });
      }
    }

    if (
      (this.props.equityPortfolio !== nextProps.equityPortfolio &&
        nextProps.equityPortfolio.data) ||
      this.props.lang != nextProps.lang
    ) {
      this.setState({
        chartOption: {
          ...this.state.chartOption,
          subtitle: {
            ...this.state.chartOption.subtitle,
            text: `<span style="color: white">${this.props.t('P/L')}</span><br/>
            <b style="color: ${this.getPLColor(
              nextProps.equityPortfolio.data?.summary.profitLoss ?? 0
            )}">${nextProps.equityPortfolio.data?.summary.PLPercent}%</b><br/>
            <b style="color: ${this.getPLColor(
              nextProps.equityPortfolio.data?.summary.profitLoss ?? 0
            )}">${formatNumber(
              nextProps.equityPortfolio.data?.summary.profitLoss,
              0
            )} </b>
            `,
          },
          series: [
            {
              name: `${this.props.t('Share')}`,
              type: 'pie',
              innerSize: '60%',
              borderWidth: 0,
              data: nextProps.equityPortfolio.data?.portfolioList.map(
                (ele, i) => {
                  return {
                    name: ele.symbol,
                    y: ele.marketValue,
                  };
                }
              ),
            },
          ] as SeriesOptionsType[],
        },
      });
    }
    return true;
  }

  render() {
    const {
      t,
      isSmallComponent,
      selectedAccount,
      equityPortfolio,
    } = this.props;
    const { isCollapsed } = this.state;
    const { data } = this.props.assetInformation;
    const accountSummary = data?.accountSummary;
    const buyingPower = data?.buyingPower;
    const cashInformation = data?.cashInformation;
    const summary = equityPortfolio.data?.summary;
    const margin = data?.margin;
    const marginCallBy = data?.marginCallBy;
    const totalStockMarketValue = data?.total?.marketValue;
    const equityMargin = [
      {
        title: 'Fixed Loan',
        value: formatNumber(
          Number(margin?.fixedLoan),
          0,
          undefined,
          false,
          '—'
        ),
      },
      {
        title: 'Day Loan',
        value: formatNumber(Number(margin?.dayLoan), 0, undefined, false, '—'),
      },
      {
        title: 'Accured Debit Interest',
        value: formatNumber(
          Number(margin?.accuredDebitInterest),
          0,
          undefined,
          false,
          '—'
        ),
      },
      {
        title: 'Stock main',
        value: formatNumber(
          Number(margin?.stockMain),
          0,
          undefined,
          false,
          '—'
        ),
      },
      {
        title: 'MAS_Equity',
        value: formatNumber(Number(margin?.equity), 0, undefined, false, '—'),
      },
      {
        title: 'Margin ratio (%)',
        value: margin?.marginRatio ?? '—',
      },
      {
        title: 'Maintenance ratio (%)',
        value: margin?.maintenanceRatio ?? '—',
      },
    ];
    if (config.hideMarginCall !== true) {
      // eslint-disable-next-line functional/immutable-data
      equityMargin.push({
        title: 'Margin call by stock main amt',
        value: formatNumber(
          marginCallBy?.marginCallByStockMainAmount,
          0,
          undefined,
          false,
          '—'
        ),
      });
      // eslint-disable-next-line functional/immutable-data
      equityMargin.push({
        title: 'Margin call by cash',
        value: formatNumber(
          marginCallBy?.marginCallByCash,
          0,
          undefined,
          false,
          '—'
        ),
      });
    }

    return isSmallComponent ? (
      <div className={styles.TabAccountMini}>
        <div>
          <p>{t('Net Asset Value')}</p>
          <p>
            {formatNumber(
              Number(accountSummary?.netAssetValue),
              0,
              undefined,
              false,
              '—'
            )}
          </p>
        </div>
        <div>
          <p>{t('Total Stock Market Value')}</p>
          <p>{formatNumber(totalStockMarketValue, 0, undefined, false, '—')}</p>
        </div>
        <div>
          <p>{t('Purchasing Power')}</p>
          <p>
            {formatNumber(
              Number(buyingPower?.purchasingPower),
              0,
              undefined,
              false,
              '—'
            )}
          </p>
        </div>
        <div>
          <p>{t('Cash Withdrawal')}</p>
          <p>
            {formatNumber(
              Number(cashInformation?.cashWithdrawal),
              0,
              undefined,
              false,
              '—'
            )}
          </p>
        </div>
        <div>
          <p>{t('Available advanced cash')}</p>
          <p>
            {formatNumber(
              Number(cashInformation?.availableAdvancedCash),
              0,
              undefined,
              false,
              '—'
            )}
          </p>
        </div>
        {/* {selectedAccount?.accountType === AccountType.MARGIN && (
          <div>
            <p>{t('Loan')}</p>
            <p>{accountSummary?.loan ?? '—'}</p>
          </div>
        )} */}
        {selectedAccount?.accountType === AccountType.MARGIN && (
          <div>
            <p>{t('Margin Call')}</p>
            <p>
              {formatNumber(
                marginCallBy?.marginCallByCash,
                0,
                undefined,
                false,
                '—'
              )}
            </p>
          </div>
        )}
      </div>
    ) : (
      //NEW_ASSETSCREEN
      <div className={styles.TabAssetInfoEquityNew}>
        <div className={styles.flex}>
          <div className={styles.TabAssetLeft}>
            {/* account_info */}
            <div className={styles.AccountInfo}>
              <div className={`${styles.Row} ${styles.mb20}`}>
                <span>{t('Account')}</span>
                <span>
                  <AccountDropdown />
                </span>
              </div>
              <div className={styles.Row}>
                <span>
                  {t('Net asset value')}
                  <OverlayTrigger
                    key={'net-asset-value'}
                    placement="top"
                    overlay={
                      <Tooltip
                        className={styles.tooltipContainer}
                        id={`tooltip-net-asset-value`}
                      >
                        {t('Net asset value tooltip')}
                      </Tooltip>
                    }
                  >
                    <InfoBuyIn width={15} />
                  </OverlayTrigger>
                </span>
                <span>{this.formatNumber(summary?.netAssetValue)}</span>
              </div>
              <div className={styles.Row}>
                <span>
                  {t('Total asset')}
                  <OverlayTrigger
                    key={'total-asset'}
                    placement="top"
                    overlay={
                      <Tooltip
                        className={styles.tooltipContainer}
                        id={`tooltip-total-asset`}
                      >
                        {selectedAccount?.accountType === AccountType.MARGIN
                          ? t('Total asset tooltip M')
                          : t('Total asset tooltip X')}
                      </Tooltip>
                    }
                  >
                    <InfoBuyIn width={15} />
                  </OverlayTrigger>
                </span>
                <span>
                  {this.formatNumber(
                    selectedAccount?.accountType === AccountType.MARGIN
                      ? summary?.totalAsset
                      : summary?.equity
                  )}
                </span>
              </div>
              <div className={styles.Row}>
                <span>{t('Buying power')}</span>
                <span>{this.formatNumber(buyingPower?.purchasingPower)}</span>
              </div>
              <div className={styles.Row}>
                <span>
                  {t('Cash Withdrawal 1')}
                  <OverlayTrigger
                    key={'cash-withdrawal'}
                    placement="top"
                    overlay={
                      <Tooltip
                        className={styles.tooltipContainer}
                        id={`tooltip-cash-withdrawal`}
                      >
                        {t('Withdrawable tooltip')}
                      </Tooltip>
                    }
                  >
                    <InfoBuyIn width={15} />
                  </OverlayTrigger>
                </span>
                <span>
                  {this.formatNumber(
                    Number(summary?.mvCashWithdrawable) >= 0
                      ? summary?.mvCashWithdrawable
                      : 0
                  )}
                </span>
              </div>

              <div className={styles.Row1}>
                <span onClick={this.onRefresh}>
                  <Refresh size={14} />
                  {t('Refresh')}
                </span>
              </div>
            </div>
            {/* Thong_tin_tien */}
            <div className={styles.Title}>
              <span>{t('Cash Information')}</span>
            </div>
            {/* Block_1 */}
            <div className={styles.Block}>
              <div className={styles.Index}>1</div>
              <div className={styles.Box}>
                <div className={styles.RowTop}>
                  <span>{t('Cash')}</span>
                  <span>
                    {selectedAccount?.accountType === AccountType.MARGIN
                      ? this.formatNumber(summary?.cManualReserve)
                      : this.formatNumber(summary?.mvSettledBalance)}
                  </span>
                </div>
              </div>
            </div>
            {/* Block_2 */}
            <div className={styles.Block}>
              <div className={styles.Index}>2</div>
              <div className={styles.Box}>
                <div className={styles.RowTop}>
                  <span>{t('Pending approval for withdrawal')}</span>
                  <span>{this.formatNumber(summary?.mvPendingWithdraw)}</span>
                </div>
              </div>
            </div>
            {/* Block_3 */}
            <div className={styles.Block}>
              <div className={styles.Index}>3</div>
              <div className={styles.Box}>
                <div className={`${styles.RowTop} ${styles.Cursor}`} onClick={() => this.onClickToggle('pendCashSold')}>
                  <span>
                    {t('Available advance money')}
                    <OverlayTrigger
                      key={'available-advance-money'}
                      placement="top"
                      overlay={
                        <Tooltip
                          className={styles.tooltipContainer}
                          id={`tooltip-available-advance-money`}
                        >
                          {t('Available advance money tooltip')}
                        </Tooltip>
                      }
                    >
                      <InfoBuyIn width={15} />
                    </OverlayTrigger>
                  </span>
                  <span>
                    {formatNumber(summary?.mvAvailAdvanceMoney)}
                    <span
                      className={styles.ml10}
                    >
                      {isCollapsed.pendCashSold ? (
                        <FaChevronUp size={14} />
                      ) : (
                        <FaChevronDown size={14} />
                      )}
                    </span>
                  </span>
                </div>
                {!isCollapsed.pendCashSold && (
                  <>
                    <div className={`${styles.Row} ${styles.mg15}`}>
                      <span>{t('Sold T0')}</span>
                      <span>{this.formatNumber(summary?.soldT0)}</span>
                    </div>
                    <div className={`${styles.Row} ${styles.mg15}`}>
                      <span>{t('Sold T1')}</span>
                      <span>{this.formatNumber(summary?.soldT1)}</span>
                    </div>
                    <div className={`${styles.Row} ${styles.mg15}`}>
                      <span>{t('Out Advance Money')}</span>
                      <span>
                        {this.formatNumber(summary?.mvTotalOutAdvance)}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
            {/* Block_4 */}
            <div className={styles.Block}>
              <div className={styles.Index}>4</div>
              <div className={styles.Box}>
                <div className={styles.RowTop}>
                  <span>
                    {t('Hold Amount Value')}
                    <OverlayTrigger
                      key={'hold-amount-value'}
                      placement="top"
                      overlay={
                        <Tooltip
                          className={styles.tooltipContainer}
                          id={`tooltip-hold-amount-value`}
                        >
                          {t(
                            'Please contact hotline at (+84-28) 3914-8585 for details on the reason of your Hold amount value.'
                          )}
                        </Tooltip>
                      }
                    >
                      <InfoBuyIn width={15} />
                    </OverlayTrigger>
                  </span>
                  <span>{this.formatNumber(summary?.mvHoldAmount)}</span>
                </div>
              </div>
            </div>
            {/* Tien_co_tuc_cho_ve*/}
            <div className={styles.Title}>
              <span>{t('Pend cash dividend')}</span>
              <span>{this.formatNumber(summary?.pendCashDiv)}</span>
            </div>

            {/* Gia_tri_chung_khoan */}
            <div className={styles.Title}>
              <span>
                {t('Market value')}
                <OverlayTrigger
                  key={'market-value'}
                  placement="top"
                  overlay={
                    <Tooltip
                      className={styles.tooltipContainer}
                      id={`tooltip-market-value`}
                    >
                      {t('Market value tooltip')}
                    </Tooltip>
                  }
                >
                  <InfoBuyIn width={15} />
                </OverlayTrigger>
              </span>
              <span>{this.formatNumber(summary?.mvMarketValue)}</span>
            </div>
            {/* Tien_mua_trong_ngay */}
            <div className={styles.Title}>
              <span>
                {t('Today order amount')}
                <OverlayTrigger
                  key={'today-order-amount'}
                  placement="top"
                  overlay={
                    <Tooltip
                      className={styles.tooltipContainer}
                      id={`tooltip-today-order-amount`}
                    >
                      {t('Today order amount tooltip')}
                    </Tooltip>
                  }
                >
                  <InfoBuyIn width={15} />
                </OverlayTrigger>
              </span>
              <span>
                {this.formatNumber(
                  (summary?.mvBuyHoldAmount ?? 0) +
                    (summary?.CTodayConfirmBuy ?? 0)
                )}
              </span>
            </div>
            {/* Block_1 */}
            <div className={styles.Block}>
              <div className={styles.Index}>1</div>
              <div className={styles.Box}>
                <div className={styles.Row}>
                  <span>{t('Buy T0')}</span>
                  <span>{this.formatNumber(summary?.mvBuyHoldAmount)}</span>
                </div>
              </div>
            </div>
            {/* Block_2 */}
            <div className={styles.Block}>
              <div className={styles.Index}>2</div>
              <div className={styles.Box}>
                <div className={styles.Row}>
                  <span>{t('Buy T0 matched')}</span>
                  <span>{this.formatNumber(summary?.CTodayConfirmBuy)}</span>
                </div>
              </div>
            </div>
            {/* Tong_no */}
            <div className={styles.Title}>
              <span>{t('Loan information')}</span>
            </div>
            {/* Block_1 */}
            <div className={styles.Block}>
              <div className={styles.Index}>1</div>
              <div className={styles.Box}>
                <div className={styles.Row}>
                  <span>{t('Loan 1')}</span>
                  <span>{this.formatNumber(summary?.mvOutstandingLoan)}</span>
                </div>
              </div>
            </div>
            {/* Block_2 */}
            <div className={styles.Block}>
              <div className={styles.Index}>2</div>
              <div className={styles.Box}>
                <div className={styles.Row}>
                  <span>{t('Debit interest 1')}</span>
                  <span>
                    {this.formatNumber(summary?.debitAccruedInterest)}
                  </span>
                </div>
              </div>
            </div>

            {/* Block_3 */}
            <div className={styles.Block}>
              <div className={styles.Index}>3</div>
              <div className={styles.Box}>
                <div className={`${styles.RowTop} ${styles.Cursor}`} onClick={() => this.onClickToggle('marginInfo')}>
                  <span>{t('Margin Information')}</span>
                  <span
                    className={styles.ml10}
                  >
                    {isCollapsed.marginInfo ? (
                      <FaChevronUp size={14} />
                    ) : (
                      <FaChevronDown size={14} />
                    )}
                  </span>
                </div>

                {!isCollapsed.marginInfo && (
                  <>
                    <div className={`${styles.Row} ${styles.mg15}`}>
                      <span>{t('Deposit to the maintenance rate')}</span>
                      <span>{this.formatNumber(summary?.cashDeposit)}</span>
                    </div>
                    <div className={`${styles.Row} ${styles.mg15}`}>
                      <span>{t('Cash Maintenance')}</span>
                      <span>{this.formatNumber(summary?.equityMar)}</span>
                    </div>
                    <div className={`${styles.Row} ${styles.mg15}`}>
                      <span>{t('Stock Maintenence')}</span>
                      <span>
                        {this.formatNumber(summary?.stockMaintenance)}
                      </span>
                    </div>
                    <div className={`${styles.Row} ${styles.mg15}`}>
                      <span>{t('Client Maintenance')}</span>
                      <span>{this.formatNumber(summary?.minMarginReq)}%</span>
                    </div>
                    <div className={`${styles.Row} ${styles.mg15}`}>
                      <span>{t('Maintenance Ratio')}</span>
                      <span>{this.formatNumber(summary?.curLiqMargin)}%</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className={styles.TabAssetRight}>
            {equityPortfolio.data?.portfolioList.length === 0 ? (
              <div className={styles.TextNoInformation}>
                {t('No Information To Show')}
              </div>
            ) : (
              <div className={styles.ChartContainer}>
                <div className={styles.ValueInfo}>
                  <div className={styles.Row}>
                    <span>{t('Total buy value')}</span>
                    <span>{formatNumber(summary?.mvSumWAC)}</span>
                  </div>
                  <div className={styles.Row}>
                    <span>{t('Total market value')}</span>
                    <span>{formatNumber(summary?.mvMarketValue)}</span>
                  </div>
                  <div className={styles.Row}>
                    <span>{t('Total Pending Entitlement Value')}</span>
                    <span>{formatNumber(summary?.mvSumEntitlementAmount)}</span>
                  </div>
                </div>
                <HighchartsReact
                  containerProps={{
                    style: { height: 400, flex: 3, minWidth: 600 },
                  }}
                  highcharts={Highcharts}
                  options={this.state.chartOption}
                  ref={this.chartRef}
                />
              </div>
            )}

            <Portfolio isAssetComponent={true} />
          </div>
        </div>
      </div>
    );
  }

  private getPLColor = (value: number) => {
    if (value < 0) {
      return 'red';
    } else if (value > 0) {
      return '#0bdf39';
    }
    return 'white';
  };

  private formatNumber = (value: string | number | undefined) => {
    if (value && typeof value === 'string') {
      return formatNumber(
        formatStringToNumber(value),
        2,
        undefined,
        false,
        '—'
      );
    } else if (value && typeof value === 'number') {
      return formatNumber(value, 2, undefined, false, '—');
    }
    return 0;
  };

  private onRefresh = () => {
    if (this.props.selectedAccount) {
      this.props.enquiryPortfolio({
        accountNumber: this.props.selectedAccount.accountNumber,
        type: this.props.selectedAccount.type,
      });
    }
  };

  private onClickToggle = (type: 'cash' | 'pendCashSold' | 'marginInfo') => {
    this.setState({
      isCollapsed: {
        ...this.state.isCollapsed,
        [type]: !this.state.isCollapsed[type],
      },
    });
  };
}

const mapStateToProps = (state: IState) => ({
  selectedAccount: state.selectedAccount,
  assetInformation: state.assetInformation,
  equityPortfolio: state.equityPortfolio,
  lang: state.lang,
});

const mapDispatchToProps = {
  getAssetInformation,
  enquiryPortfolio,
};

const AssetInforEquity = withRouter(
  withErrorBoundary(
    withNamespaces('common')(
      connect(mapStateToProps, mapDispatchToProps)(AssetInforEquityComponent)
    ),
    Fallback,
    handleError
  )
);

export default AssetInforEquity;



// WEBPACK FOOTER //
// ./src/components/areas/Account/AssetInformation/AssetInforEquity/index.tsx